/**
 * use-referral-actions.ts — React hook encapsulating coordinator action API calls.
 *
 * Provides confirmProvider, rejectReferral, notifyIneligible — each returns
 * { execute, isPending, error } for use in dialog components.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import { useState, useCallback } from 'react';

import { referralApi } from './referral-api.js';

export interface ActionState {
  isPending: boolean;
  error: Error | null;
}

export interface UseReferralActionsReturn {
  confirmProvider: {
    execute: (referralId: string) => Promise<void>;
    isPending: boolean;
    error: Error | null;
  };
  rejectReferral: {
    execute: (referralId: string, reason: string) => Promise<void>;
    isPending: boolean;
    error: Error | null;
  };
  notifyIneligible: {
    execute: (referralId: string) => Promise<void>;
    isPending: boolean;
    error: Error | null;
  };
}

export function useReferralActions(): UseReferralActionsReturn {
  const auth = usePortalAuth();

  const [confirmState, setConfirmState] = useState<ActionState>({ isPending: false, error: null });
  const [rejectState, setRejectState] = useState<ActionState>({ isPending: false, error: null });
  const [notifyState, setNotifyState] = useState<ActionState>({ isPending: false, error: null });

  const confirmProviderFn = useCallback(
    async (referralId: string): Promise<void> => {
      setConfirmState({ isPending: true, error: null });
      try {
        const token = auth.user?.access_token;
        await referralApi.confirmProvider(referralId, token);
        setConfirmState({ isPending: false, error: null });
      } catch (err) {
        setConfirmState({
          isPending: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
        throw err; // re-throw so callers can handle success/error
      }
    },
    [auth],
  );

  const rejectReferralFn = useCallback(
    async (referralId: string, reason: string): Promise<void> => {
      setRejectState({ isPending: true, error: null });
      try {
        const token = auth.user?.access_token;
        await referralApi.rejectReferral(referralId, reason, token);
        setRejectState({ isPending: false, error: null });
      } catch (err) {
        setRejectState({
          isPending: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
        throw err;
      }
    },
    [auth],
  );

  const notifyIneligibleFn = useCallback(
    async (referralId: string): Promise<void> => {
      setNotifyState({ isPending: true, error: null });
      try {
        const token = auth.user?.access_token;
        await referralApi.notifyIneligible(referralId, token);
        setNotifyState({ isPending: false, error: null });
      } catch (err) {
        setNotifyState({
          isPending: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
        throw err;
      }
    },
    [auth],
  );

  return {
    confirmProvider: {
      execute: confirmProviderFn,
      isPending: confirmState.isPending,
      error: confirmState.error,
    },
    rejectReferral: {
      execute: rejectReferralFn,
      isPending: rejectState.isPending,
      error: rejectState.error,
    },
    notifyIneligible: {
      execute: notifyIneligibleFn,
      isPending: notifyState.isPending,
      error: notifyState.error,
    },
  };
}
